import { Button } from '@edsn/pie-components';
import AddIcon from '@iconify/icons-material-symbols/add';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import {
  getGetTeamIdQueryKey,
  usePostContactDetailsIdTeam,
} from '#pie/internal/api/pie-bff';
import { Icon } from '../icon/Icon';
import { Stack } from '../stack/Stack';
import { useToast } from '../toast/ToastContext';
import { FormUserCombobox } from '../user-combobox/FormUserCombobox';

interface FormAddTeamMember {
  userId: string;
}

interface Props {
  teamId: string;
}

export const AddTeamMemberModal = ({ teamId }: Props) => {
  const { t: tcom } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { addToast } = useToast();

  const formMethods = useForm<FormAddTeamMember>();

  const { handleSubmit } = formMethods;

  const { mutate, isPending: isLoading } = usePostContactDetailsIdTeam({
    mutation: {
      onError: (error) => {
        addToast({
          message: tcom(
            'cpr_contact_group_detail.add_user.toast.error.message',
          ),
          title: tcom('cpr_contact_group_detail.add_user.toast.error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: getGetTeamIdQueryKey(teamId),
        });
        queryClient.removeQueries({
          queryKey: ['/contact/details'],
        });
        /* istanbul ignore next -- @preserve */
        queryClient.invalidateQueries({
          predicate: (m) => {
            return m.queryKey.some(
              (n) => typeof n === 'string' && n.includes(variables.id),
            );
          },
        });
        formMethods.reset();
        addToast({
          message: tom('toast.success.title_user_added_succesfully'),
          title: tcom('common.new_user_added'),
          type: 'success',
        });
        setIsOpen(false);
      },
    },
  });

  const onSubmit: SubmitHandler<FormAddTeamMember> = (data) => {
    mutate({ data: { teamId }, id: data.userId });
  };

  return (
    <>
      <Button
        action="secondary"
        emphasis="bold"
        iconStart={<Icon icon={AddIcon} />}
        onClick={() => setIsOpen(true)}
      >
        {tom('button.add_user')}
      </Button>

      {isOpen && (
        <Dialog
          title={tom('add_user')}
          size="md"
          open={isOpen}
          onOpenChange={setIsOpen}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="lg">
                <FormUserCombobox
                  label={tom('dialog_title.choose_user')}
                  name="userId"
                  rules={{ required: true }}
                  fullWidth
                  filter={{ excludeTeamIds: [teamId] }}
                />
                <Button
                  isLoading={isLoading}
                  type="submit"
                  action="secondary"
                  emphasis="bold"
                >
                  {tom('add_user')}
                </Button>
              </Stack>
            </form>
          </FormProvider>
        </Dialog>
      )}
    </>
  );
};
